import type { ScenarioSpec } from "./types";
import { phishingOfficeScenario as PHISH } from "./phishing-office.scenario";

/** The computer for Send It Safely. Opened from the sharing-files terminal. */
export const sendItSafelyScenario: ScenarioSpec = {
  version: 1,
  id: "send-it-safely",
  title: "Send It Safely",
  summary: {
    goal: "Learn to share each file with the right people, give only the access a task needs, check app permission requests and remove access when work ends.",
    youDo: "Read requests in Mail, find the right file, pick recipients, set permissions and link types, review two apps asking to connect, and close access when the workshop is over.",
    duration: "5-8 min, optional tasks add 4-6 min",
    tags: ["file_sharing", "data_protection", "security_policies"],
    audience: ["all_staff"],
  },
  topic: "Help a small team organise a workplace workshop",
  language: "en",
  passThreshold: 60,
  allowFreeExplore: false,
  voiceAttackers: PHISH.voiceAttackers,
  // Own camera: closer to the monitor so the screen is 78vh, not the shared 62vh.
  scene: {
    ...PHISH.scene,
    environment: { ...PHISH.scene.environment, procedural: "sendit_desk" },
    waypoints: PHISH.scene.waypoints.map((w) =>
      w.id === "wp_monitor"
        ? { ...w, position: [0, 0.95, 0.34], target: [0, 0.95, -0.33], fov: 36 }
        : w,
    ),
  },
  devices: [
    {
      id: "send_pc",
      kind: "sendit",
      config: { title: "Send It Safely" },
    },
  ],
  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "Send It Safely",
      kicker: "Workshop",
      guide: {
        title: "Start",
        body: "You're helping a small team organise a workplace workshop. Requests arrive in Mail asking you to share files with colleagues, an outside facilitator or the public. It starts at your desk computer when the first request comes in.",
      },
      ctaLabel: "Start at the computer",
    },
    { id: "b_to_monitor", type: "camera", waypointId: "wp_monitor" },
    {
      id: "b_game",
      type: "device",
      deviceId: "send_pc",
      guide: {
        title: "Send It Safely",
        body: "Open Mail, check the file, then choose who can access it and what they can do.",
      },
      completeOn: { action: "continue" },
    },
    {
      id: "b_quiz",
      type: "quiz",
      title: "Check what you did",
      quiz: [
        {
          id: "q_morgan",
          title: "Question 1",
          prompt: "Two people are named Morgan Lee. The request is for the colleague in your organisation. What do you check?",
          options: [
            { id: "a", label: "The first name in the list is enough.", icon: "x" },
            { id: "b", label: "The full address and organisation on the trusted directory card.", correct: true, icon: "check" },
            { id: "c", label: "Send it to both, so one of them is right.", icon: "alert" },
          ],
          rationale: "The same name can belong to two people. The address and organisation identify the colleague.",
        },
        {
          id: "q_copy",
          title: "Question 2",
          prompt: "An external facilitator needs to suggest changes. Which file do you share?",
          options: [
            { id: "a", label: "The internal working copy, because it has the latest comments.", icon: "x" },
            { id: "b", label: "The version marked approved for external review.", correct: true, icon: "check" },
            { id: "c", label: "A public link, so the invitation cannot fail.", icon: "alert" },
          ],
          rationale: "The working copy has internal notes and personal contact details. The prepared external version is the one for the facilitator.",
        },
        {
          id: "q_folder",
          title: "Question 3",
          prompt: "You invite the project team to edit a schedule, but a former collaborator can still open it. Why?",
          options: [
            { id: "a", label: "A new invitation replaces every older path.", icon: "x" },
            { id: "b", label: "The file still inherits access from a broader folder.", correct: true, icon: "check" },
            { id: "c", label: "View access expires on its own at the end of the day.", icon: "alert" },
          ],
          rationale: "A new narrow share does not cancel access that already comes from the parent folder.",
        },
        {
          id: "q_public",
          title: "Question 4",
          prompt: "The flyer is marked approved for public distribution. A link that still requires sign-in will:",
          options: [
            { id: "a", label: "Always be the safer choice, so the task is complete.", icon: "x" },
            { id: "b", label: "Stop the intended audience from opening it without an account.", correct: true, icon: "check" },
            { id: "c", label: "Publish the file in search engines.", icon: "alert" },
          ],
          rationale: "This task needs anyone with the link to read it. A restricted link is not a leak, but it does not do this job.",
        },
        {
          id: "q_app",
          title: "Question 5",
          prompt: "An unexpected viewer asks for all files, mailbox reading, and the ability to send mail. What do you do?",
          options: [
            { id: "a", label: "Approve it. The page looks like a real provider.", icon: "x" },
            { id: "b", label: "Cancel it and report it. Use the approved helper for the selected document only.", correct: true, icon: "check" },
            { id: "c", label: "Change your password. That removes the app.", icon: "alert" },
          ],
          rationale: "A polished consent page is not employer approval. Password change does not remove an app grant. Reporting starts that removal.",
        },
        {
          id: "q_close",
          title: "Question 6",
          prompt: "The facilitator's work is finished. What should stay in place?",
          options: [
            { id: "a", label: "Nothing. Delete every file and the public flyer link.", icon: "x" },
            { id: "b", label: "The team's schedule access and the public flyer. Remove only the facilitator's remaining access.", correct: true, icon: "check" },
            { id: "c", label: "The facilitator's guest membership, in case they come back.", icon: "alert" },
          ],
          rationale: "Close the access the task no longer needs. Keep the team's files and the flyer that was meant to stay public. A copy they already downloaded can remain on their side.",
        },
      ],
    },
  ],
  edges: [
    { from: "b_intro", to: "b_to_monitor" },
    { from: "b_to_monitor", to: "b_game" },
    { from: "b_game", to: "b_quiz" },
  ],
  initialBeatId: "b_intro",
};
